import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Avatar, Box, Button, Chip, CircularProgress, Divider, IconButton, InputBase, LinearProgress, List, ListItem, ListItemAvatar, ListItemText, Paper, Snackbar, Tooltip, Typography, Dialog } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ForumIcon from '@mui/icons-material/ForumOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PdfIconSvg from '../../assets/images/picture_as_pdf.svg';
import { ChatConversation, ChatMessage, listMessages, sendMessage, markRead, uploadFile, listMessagesAfter, reactMessage, unreactMessage, editMessage, ReactionType, postGemmaAssistantMessage } from '../../services/internalChatService';
import { agentChatStream, agentChatStreamWithFiles, type AgentMysqlStreamEvent } from '../../services/deepseekService';
import GemmaAgentActivityPanel, { GemmaActivityLine } from './GemmaAgentActivityPanel';
import {
  appendGemmaExchange,
  bumpGemmaSessionExpiryIfActive,
  extractGemmaQueryForSend,
  extractStickyProjectIdFromGemmaTurns,
  loadGemmaTurnsForApi,
} from '../../utils/internalGemmaSession';
import { stripGemmaInlineKzmPdfMarker } from '../../utils/gemmaInlineKzmPdf';
import { parseGemmaInlineOptions } from '../../utils/gemmaInlineOptions';
import GemmaInlineOptions from './GemmaInlineOptions';
import VoiceInputButton from './VoiceInputButton';
import { useVoiceDictation } from '../../hooks/useVoiceDictation';
import { onInternalChatNewMessage, onInternalChatRead, onInternalChatReaction, onInternalChatMessageEdited } from '../../services/realtimeService';
import { formatMessageHtml } from '../../utils/linkify';
import { getBoardUrl } from '../../services/collectifileService';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as SendSvg } from '../../assets/images/send.svg';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import AddReactionIcon from '@mui/icons-material/AddReactionOutlined';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { MentionsInput, Mention } from 'react-mentions';
import GemmaAvatar from './GemmaAvatar';
import { GEMMA_ICON_URL, isGemmaMessage, isInternalChatGemmaSystemUserId, resolveGemmaMessageAvatarUrl } from '../../constants/gemmaIcon';
import {
  fetchGemmaMentionData,
  GEMMA_MENTION_MARKUP_REGEX,
  getGemmaMentionTypingHint,
  internalChatComposerPlainText,
  ICC_COMPOSER_FONT_PX,
  ICC_COMPOSER_FONT_STACK,
  ICC_COMPOSER_INPUT_PADDING,
  ICC_COMPOSER_LINE_HEIGHT_PX,
  internalChatMentionsStyles,
} from '../../utils/internalChatMentions';

/** Dymek Gemmy — pastelowy róż (odróżnienie od zielonego „ja” i żółtego „inni”). */
const INTERNAL_CHAT_GEMMA_BUBBLE_BG = '#F5E6EE';

/** Stałe sx — memo poniżej nie dostaje nowych referencji przy każdym renderze rodzica. */
const INTERNAL_CHAT_FORMATTED_SX = {
  bubble: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    '& a': { wordBreak: 'break-all' },
    '& .internal-chat-mention-gemma': { color: '#3F3FDC', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: '2px' },
  },
  boardInline: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    '& a': { wordBreak: 'break-all' },
  },
  replyQuote: {
    color: '#595966',
    fontSize: 13,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    mt: 0.25,
    '& a': { wordBreak: 'break-all' },
  },
  /** Treść odpowiedzi pod cytatem — bez stylu @gemma (jak wcześniejszy Typography). */
  replyBody: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    '& a': { wordBreak: 'break-all' },
  },
} as const;

type InternalChatFormattedPreset = keyof typeof INTERNAL_CHAT_FORMATTED_SX;

/**
 * Linkifikacja tylko gdy zmienia się `text` — przy streamie @gemma rodzic renderuje się co klatkę,
 * ale pozostałe wiadomości nie ponoszą kosztu formatMessageHtml.
 */
const InternalChatFormattedHtml = React.memo(
  function InternalChatFormattedHtml({
    text,
    preset,
    component = 'p',
  }: {
    text: string;
    preset: InternalChatFormattedPreset;
    component?: React.ElementType;
  }) {
    const html = useMemo(() => formatMessageHtml(text), [text]);
    return (
      <Typography
        component={component}
        variant="body2"
        sx={INTERNAL_CHAT_FORMATTED_SX[preset]}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  },
  (a, b) => a.text === b.text && a.preset === b.preset && a.component === b.component,
);

/** Treść bąbelka Gemmy: usuwa znacznik PDF KZM, parsuje klikalne opcje i pokazuje przyciski (jak w AIDialog). */
const GemmaBubbleWithKzmPdfButton = React.memo(
  function GemmaBubbleWithKzmPdfButton({
    text,
    preset,
    onPickKzmPdf,
    pickDisabled,
    isLastGemmaBubble,
    onPickOption,
    pickOptionsDisabled,
  }: {
    text: string;
    preset: InternalChatFormattedPreset;
    onPickKzmPdf: () => void;
    pickDisabled: boolean;
    isLastGemmaBubble?: boolean;
    onPickOption?: (value: string) => void;
    pickOptionsDisabled?: boolean;
  }) {
    const { displayText: afterPdfStrip, showInlinePdfAttach } = stripGemmaInlineKzmPdfMarker(text);
    const { displayText, groups: inlineOptionGroups } = parseGemmaInlineOptions(afterPdfStrip);
    const showInlineOptions = Boolean(isLastGemmaBubble && onPickOption && inlineOptionGroups.length > 0);
    return (
      <>
        <InternalChatFormattedHtml text={displayText} preset={preset} />
        {showInlinePdfAttach && (
          <Box
            sx={{
              mt: 1,
              pt: 1,
              borderTop: '1px solid rgba(2,2,66,0.12)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
              <Box
                component="button"
                type="button"
                onClick={() => {
                  if (!pickDisabled) onPickKzmPdf();
                }}
                disabled={pickDisabled}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  border: '1px solid rgba(63,63,220,0.35)',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(63,63,220,0.06)',
                  color: '#020242',
                  cursor: pickDisabled ? 'default' : 'pointer',
                  py: 0.5,
                  px: 1,
                  font: 'inherit',
                  fontSize: 13,
                  '&:hover': pickDisabled ? {} : { backgroundColor: 'rgba(63,63,220,0.1)' },
                }}
              >
                <AttachFileIcon sx={{ fontSize: 18, transform: 'rotate(45deg)', color: '#6B6BD6' }} />
                <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
                  Dodaj PDF do KZM
                </Typography>
              </Box>
          </Box>
        )}
        {showInlineOptions && (
          <GemmaInlineOptions
            groups={inlineOptionGroups}
            disabled={!!pickOptionsDisabled}
            onPick={(value) => {
              if (!pickOptionsDisabled && onPickOption) onPickOption(value);
            }}
          />
        )}
      </>
    );
  },
  (a, b) =>
    a.text === b.text &&
    a.preset === b.preset &&
    a.pickDisabled === b.pickDisabled &&
    a.onPickKzmPdf === b.onPickKzmPdf &&
    a.isLastGemmaBubble === b.isLastGemmaBubble &&
    a.pickOptionsDisabled === b.pickOptionsDisabled &&
    a.onPickOption === b.onPickOption,
);

function formatYmdHms(iso?: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso as string);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}, ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch {
    return String(iso ?? '');
  }
}

function parseBoardCommentMessage(text: string): { prefix: string; projectId: string; commentId: string } | null {
  const match = text.match(/^(.+?)\s*\[BOARD:([^:]+):([^\]]+)\]$/s);
  if (!match) return null;
  return { prefix: match[1].trim(), projectId: match[2], commentId: match[3] };
}

function parseReplyMessage(text: string): { quoteAuthor: string; quoteText: string; replyText: string } | null {
  const parts = text.split(/\n\n+/);
  if (parts.length < 2) return null;
  const replyText = parts.slice(1).join('\n\n').trim();
  if (!replyText) return null;
  const firstPart = parts[0];
  if (!firstPart.startsWith('> ')) return null;
  const afterArrow = firstPart.slice(2);
  const colonIdx = afterArrow.indexOf(': ');
  if (colonIdx < 0) return null;
  return {
    quoteAuthor: afterArrow.slice(0, colonIdx).trim(),
    quoteText: afterArrow.slice(colonIdx + 2).trim(),
    replyText,
  };
}

function normReplyText(s: string): string {
  return String(s || '')
    .trim()
    .replace(/\r\n/g, '\n')
    .replace(/\u00a0/g, ' ');
}

/** Tylko trwałe id z bazy — nie scalaj dwóch prawdziwych wiadomości o tej samej treści. */
function isNumericDbMessageId(id: unknown): boolean {
  return /^\d+$/.test(String(id ?? ''));
}

/** Realtime: bot ma numeryczny user_id z bazy, jeśli brak REACT_APP_INTERNAL_CHAT_GEMMA_BOT_USER_ID. */
function isSocketPayloadFromGemmaAssistant(p: {
  fromUserId?: string | null;
  senderDisplayName?: string | null;
}): boolean {
  if (isInternalChatGemmaSystemUserId(p.fromUserId)) return true;
  return String(p.senderDisplayName || '').trim().toLowerCase() === 'gemma';
}

function isOurGemmaReplyBubble(m: ChatMessage, replyNorm: string): boolean {
  if (normReplyText(m.text || '') !== replyNorm) return false;
  if (isGemmaMessage(m)) return true;
  const id = String(m.id);
  if (id.startsWith('gemma-pending-') || id.startsWith('gemma-local-')) return true;
  const dn = (m.sender?.displayName || '').trim().toLowerCase();
  return dn === 'gemma';
}

/**
 * Scalanie po zapisie Gemmy: sync może być „za stary”; socket mógł już zamienić pending na prawdziwe id —
 * wtedy prev nie ma gemma-pending-*, a wpis znikałby przy samym sync.items.
 */
function mergeGemmaReplyWithServerList(
  prev: ChatMessage[],
  serverItems: ChatMessage[],
  _pendingId: string,
  reply: string,
  pinnedFromApi?: ChatMessage | null,
): ChatMessage[] {
  const replyNorm = normReplyText(reply);
  const serverIds = new Set(serverItems.map((m) => String(m.id)));

  const serverHas = serverItems.some(
    (m) => isGemmaMessage(m) && normReplyText(m.text || '') === replyNorm,
  );
  if (serverHas) return serverItems;

  const pinList: ChatMessage[] = [];
  if (
    pinnedFromApi &&
    normReplyText(pinnedFromApi.text || '') === replyNorm &&
    !serverIds.has(String(pinnedFromApi.id))
  ) {
    pinList.push(pinnedFromApi);
  }

  const missing = prev.filter((m) => {
    if (serverIds.has(String(m.id))) return false;
    if (pinList.length && isOurGemmaReplyBubble(m, replyNorm)) return false;
    return isOurGemmaReplyBubble(m, replyNorm);
  });

  const combined = [...serverItems, ...pinList, ...missing];
  const seen = new Set<string>();
  const out: ChatMessage[] = [];
  for (const m of combined) {
    const id = String(m.id);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(m);
  }
  return out.sort((a, b) => {
    const na = Number.parseInt(String(a.id), 10);
    const nb = Number.parseInt(String(b.id), 10);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return 0;
  });
}

function lastNumericMessageId(messages: ChatMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const id = String(messages[i].id);
    if (/^\d+$/.test(id)) return id;
  }
  return null;
}

interface Props {
  activeConversation?: ChatConversation | null;
  showHeader?: boolean;
}

export default function InternalChatPanel({ activeConversation, showHeader = true }: Props) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const voiceDictation = useVoiceDictation({
    lang: 'pl-PL',
    value: text,
    onChangeValue: setText,
  });
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  /** Synchroniczny guard — React `sending` bywa opóźniony; bez tego drugie Enter/klik w trakcie wysyłki wiadomości odpala równoległe handleSend. */
  const sendingRef = useRef(false);
  /** Inkrementowany przy każdym tle-sync po wysłaniu — odrzuca przestarzałe `listMessages`, gdy użytkownik wyśle kilka wiadomości pod rząd. */
  const postSendSyncSeqRef = useRef(0);
  const activeConversationIdRef = useRef<string | null>(null);
  activeConversationIdRef.current = activeConversation?.id != null ? String(activeConversation.id) : null;
  /** True tylko podczas streamu SSE agenta — zapis do API/merge leci w tle, bez blokowania kolejnego @gemma. */
  const gemmaPipelineRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  /** Po wejściu w rozmowę krótko wymuszaj dół przy zmianie scrollHeight (obrazy, fonty, layout Drawera). */
  const stickToBottomUntilRef = useRef<number>(0);
  /** Agregacja assistant_delta → max 1 setMessages na klatkę (unikaj zawieszenia przy setkach tokenów/s). */
  const gemmaDeltaRafRef = useRef<number | null>(null);
  const gemmaDeltaAccumRef = useRef('');
  const gemmaDeltaTargetIdRef = useRef<string | null>(null);
  /** Max 1 scrollTo na ~80 ms podczas streamu — mniej layoutów przy długiej liście. */
  const gemmaStreamScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gemmaActivityPendingRef = useRef<GemmaActivityLine[]>([]);
  const gemmaActivityFlushRafRef = useRef<number | null>(null);
  /** Tekst streamu Gemmy bez setMessages — tylko textContent w <span> (unikaj re-renderu całej listy co klatkę). */
  const gemmaStreamTextElRef = useRef<HTMLSpanElement | null>(null);
  const [gemmaLiveStreamMessageId, setGemmaLiveStreamMessageId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { user } = useAuth();
  const isFieldWorker = user?.role === 'field_worker';
  const [readUpToOther, setReadUpToOther] = useState<string | null>(null);
  const [openReactionsForId, setOpenReactionsForId] = useState<string | null>(null);
  const [customAddIconOk, setCustomAddIconOk] = useState<boolean>(true);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadFileName, setUploadFileName] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [gemmaThinking, setGemmaThinking] = useState(false);
  const [gemmaActivityLines, setGemmaActivityLines] = useState<GemmaActivityLine[]>([]);
  const [gemmaActivityExpanded, setGemmaActivityExpanded] = useState(false);
  const [gemmaSaveWarn, setGemmaSaveWarn] = useState<string | null>(null);
  /** PDF pod KZM w czacie wewnętrznym — tylko `agentChatStreamWithFiles` (jak drawer Gemma). */
  const [kzmStagingPdfFiles, setKzmStagingPdfFiles] = useState<File[]>([]);
  const [icComposerError, setIcComposerError] = useState<string | null>(null);
  const kzmGemmaPdfInputRef = useRef<HTMLInputElement | null>(null);
  const MAX_KZM_IC_PDF = 8;

  const flushGemmaActivityLines = useCallback(() => {
    gemmaActivityFlushRafRef.current = null;
    const batch = gemmaActivityPendingRef.current;
    if (batch.length === 0) return;
    gemmaActivityPendingRef.current = [];
    setGemmaActivityLines((prev) => [...prev.map((l) => ({ ...l, isLive: false })), ...batch].slice(-35));
  }, []);

  const pushGemmaActivityLine = useCallback(
    (verb: string, detail?: string) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      gemmaActivityPendingRef.current.push({ id, verb, detail, isLive: true });
      if (gemmaActivityFlushRafRef.current == null) {
        gemmaActivityFlushRafRef.current = requestAnimationFrame(flushGemmaActivityLines);
      }
    },
    [flushGemmaActivityLines],
  );

  /** Ostatnie linie „Gemma — aktywność” z bufora RAF — bez tego zdarzenia giną, gdy stream kończy się przed klatką. */
  const flushPendingGemmaActivitySync = useCallback(() => {
    const batch = gemmaActivityPendingRef.current;
    if (batch.length === 0) return;
    gemmaActivityPendingRef.current = [];
    setGemmaActivityLines((prev) => [...prev.map((l) => ({ ...l, isLive: false })), ...batch].slice(-35));
  }, []);

  const pickKzmPdfForGemma = useCallback(() => {
    if (isFieldWorker) return;
    if (!sending && !gemmaThinking) kzmGemmaPdfInputRef.current?.click();
  }, [sending, gemmaThinking, isFieldWorker]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const editInputRef = useRef<HTMLTextAreaElement | null>(null);

  const title = useMemo(() => activeConversation?.name || 'Rozmowa', [activeConversation?.name]);
  /** Ta sama logika co w handleSend — samo `text.trim()` psuje canSend przy markupie react-mentions. */
  const composerHasSendableText = useMemo(() => {
    const raw = text.trim();
    if (!raw) return false;
    const plain = internalChatComposerPlainText(raw).trim();
    return (plain.length > 0 ? plain : raw).length > 0;
  }, [text]);
  const gemmaTypingHint = useMemo(
    () => (isFieldWorker ? null : getGemmaMentionTypingHint(text)),
    [text, isFieldWorker],
  );
  const mentionData = useCallback(
    (query: string, callback: (data: { id: string; display: string }[]) => void) => {
      if (isFieldWorker) {
        callback([]);
        return;
      }
      fetchGemmaMentionData(query, callback);
    },
    [isFieldWorker],
  );
  const [gemmaGhostScroll, setGemmaGhostScroll] = useState({ left: 0, top: 0 });

  useEffect(() => {
    sendingRef.current = false;
    gemmaPipelineRef.current = false;
    postSendSyncSeqRef.current = 0;
    setSending(false);
    return () => {
      sendingRef.current = false;
      gemmaPipelineRef.current = false;
    };
  }, [activeConversation?.id]);

  useEffect(() => {
    if (!gemmaTypingHint) return;
    const sync = () => {
      const ta = textInputRef.current;
      if (ta) setGemmaGhostScroll({ left: ta.scrollLeft, top: ta.scrollTop });
    };
    const tid = window.setTimeout(() => {
      const ta = textInputRef.current;
      if (!ta) return;
      sync();
      ta.addEventListener('scroll', sync, { passive: true });
    }, 0);
    return () => {
      window.clearTimeout(tid);
      textInputRef.current?.removeEventListener('scroll', sync);
    };
  }, [gemmaTypingHint, text, activeConversation?.id]);

  useEffect(() => {
    return () => {
      if (gemmaDeltaRafRef.current != null) {
        cancelAnimationFrame(gemmaDeltaRafRef.current);
        gemmaDeltaRafRef.current = null;
      }
      if (gemmaActivityFlushRafRef.current != null) {
        cancelAnimationFrame(gemmaActivityFlushRafRef.current);
        gemmaActivityFlushRafRef.current = null;
      }
      if (gemmaStreamScrollTimerRef.current != null) {
        clearTimeout(gemmaStreamScrollTimerRef.current);
        gemmaStreamScrollTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let off: any;
    let offRead: any;
    let offReact: any;
    let offEdit: any;

    const cidRaw = activeConversation?.id != null ? String(activeConversation.id) : null;
    if (!cidRaw) {
      setMessages([]);
      setNextPageToken(null);
      return () => {};
    }
    const cid: string = cidRaw;
    bumpGemmaSessionExpiryIfActive(cid);

    async function load(initial = false) {
      if (initial) setLoading(true);
      try {
        const { items, nextPageToken: token, readUpToByOthers } = await listMessages(cid);
        if (cancelled) return;
        setMessages(items);
        setNextPageToken(token);
        setReadUpToOther(readUpToByOthers || null);
        try {
          await markRead(cid, items.length ? items[items.length - 1].id : undefined);
        } catch {}
      } finally {
        if (!cancelled && initial) setLoading(false);
      }
    }

    Promise.resolve().then(() => load(true));

    off = onInternalChatNewMessage((p) => {
      if (String(p.conversationId) !== cid) return;
      setMessages((prev) => {
        if (prev.some((m) => String(m.id) === String(p.messageId))) return prev;
        const incomingNorm = normReplyText(String(p.content || ''));
        const matchPending = prev.find(
          (m) => String(m.id).startsWith('gemma-pending-') && normReplyText(m.text || '') === incomingNorm,
        );
        if (matchPending) {
          const next = [
            ...prev.filter((m) => m.id !== matchPending.id),
            {
              id: String(p.messageId),
              text: p.content,
              createTime: p.createdAt || new Date().toISOString(),
              sender: { displayName: p.senderDisplayName || undefined, email: undefined, avatarUrl: p.senderAvatarUrl || null },
              senderId: p.fromUserId,
            } as ChatMessage,
          ];
          setTimeout(() => { scrollRef.current?.scrollTo({ top: 999999, behavior: 'auto' }); }, 0);
          return next;
        }
        /* Socket przyszedł, a matchPending nie złapał (np. różnica białych znaków) — zamiast drugiego bąbla
         * zamień prowizoryczny wpis Gemmy (pending/local), o ile treść po normalizacji jest taka sama. */
        if (isSocketPayloadFromGemmaAssistant(p)) {
          const dupIdx = prev.findIndex(
            (m) =>
              isGemmaMessage(m) &&
              String(m.id) !== String(p.messageId) &&
              !isNumericDbMessageId(m.id) &&
              normReplyText(m.text || '') === incomingNorm,
          );
          if (dupIdx >= 0) {
            const next = [...prev];
            next[dupIdx] = {
              id: String(p.messageId),
              text: p.content,
              createTime: p.createdAt || new Date().toISOString(),
              sender: { displayName: p.senderDisplayName || undefined, email: undefined, avatarUrl: p.senderAvatarUrl || null },
              senderId: p.fromUserId,
            } as ChatMessage;
            setTimeout(() => { scrollRef.current?.scrollTo({ top: 999999, behavior: 'auto' }); }, 0);
            return next;
          }
        }
        const next = [
          ...prev,
          {
            id: p.messageId,
            text: p.content,
            createTime: p.createdAt || new Date().toISOString(),
            sender: { displayName: p.senderDisplayName || undefined, email: undefined, avatarUrl: p.senderAvatarUrl || null },
            senderId: p.fromUserId,
          } as ChatMessage,
        ];
        setTimeout(() => { scrollRef.current?.scrollTo({ top: 999999, behavior: 'auto' }); }, 0);
        return next;
      });
      try {
        markRead(cid).catch(() => {});
      } catch {}
    });
    offRead = onInternalChatRead((p) => {
      if (String(p.conversationId) !== cid) return;
      if (String(p.fromUserId) !== String(user?.user_id)) {
        setReadUpToOther(p.lastReadMessageId || null);
      }
    });
    offReact = onInternalChatReaction((p) => {
      if (String(p.conversationId) !== cid) return;
      setMessages((prev) =>
        prev.map((msg) => {
          if (String(msg.id) !== String(p.messageId)) return msg;
          const counts: Record<ReactionType, number> = {
            up: msg.reactions?.up || 0,
            down: msg.reactions?.down || 0,
            smile: msg.reactions?.smile || 0,
            heart: msg.reactions?.heart || 0,
            haha: msg.reactions?.haha || 0,
            exclamation: msg.reactions?.exclamation || 0,
            question: msg.reactions?.question || 0,
          };
          const isSelf = String(p.fromUserId || '') === String(user?.user_id || '');
          if (isSelf) {
            const prevMy = msg.myReaction || null;
            if (prevMy) counts[prevMy as ReactionType] = Math.max(0, counts[prevMy as ReactionType] - 1);
            if (p.reaction) counts[p.reaction as ReactionType] = (counts[p.reaction as ReactionType] || 0) + 1;
            return { ...msg, reactions: counts, myReaction: (p.reaction as any) ?? null };
          }
          if (p.reaction) {
            counts[p.reaction as ReactionType] = (counts[p.reaction as ReactionType] || 0) + 1;
          }
          return { ...msg, reactions: counts };
        }),
      );
    });
    offEdit = onInternalChatMessageEdited((p) => {
      if (String(p.conversationId) !== cid) return;
      setMessages((prev) =>
        prev.map((msg) => {
          if (String(msg.id) !== String(p.messageId)) return msg;
          return { ...msg, text: p.content };
        }),
      );
    });
    return () => {
      cancelled = true;
      try {
        off?.();
        offRead?.();
        offReact?.();
        offEdit?.();
      } catch {}
    };
    }, [activeConversation?.id]);

  useEffect(() => {
    setKzmStagingPdfFiles([]);
    setIcComposerError(null);
  }, [activeConversation?.id]);

  function snapScrollToBottom() {
    const el = scrollRef.current;
    if (!el) return;
    try {
      el.scrollTop = el.scrollHeight;
      messagesEndRef.current?.scrollIntoView({ block: 'end', behavior: 'auto' });
    } catch {
      /* noop */
    }
  }

  useLayoutEffect(() => {
    if (loading) return;
    snapScrollToBottom();
    requestAnimationFrame(() => {
      snapScrollToBottom();
      requestAnimationFrame(snapScrollToBottom);
    });
    stickToBottomUntilRef.current = Date.now() + 600;
  }, [loading, activeConversation?.id]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (Date.now() > stickToBottomUntilRef.current) return;
      try {
        el.scrollTop = el.scrollHeight;
      } catch {
        /* noop */
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeConversation?.id]);

  async function loadOlder() {
    if (!activeConversation || !nextPageToken) return;
    const { items, nextPageToken: token } = await listMessages(activeConversation.id, nextPageToken);
    const el = scrollRef.current; const prev = el?.scrollHeight || 0;
    setMessages(prevMsgs => [...items, ...prevMsgs]);
    setNextPageToken(token);
    setTimeout(() => { if (!el) return; el.scrollTop = (el.scrollHeight - prev) + (el.scrollTop || 0); }, 0);
  }

  async function handleSend(overrideText?: string) {
    const valueRaw = (overrideText ?? text).trim();
    if (!valueRaw || !activeConversation) return;
    const valuePlain = internalChatComposerPlainText(valueRaw).trim();
    const value = valuePlain.length > 0 ? valuePlain : valueRaw;
    const savedReplyingTo = replyingTo;
    const senderName = savedReplyingTo ? (savedReplyingTo.sender?.displayName || savedReplyingTo.sender?.email || 'Użytkownik') : '';
    const quotedText = savedReplyingTo ? (savedReplyingTo.text || savedReplyingTo.fileName || '[załącznik]') : '';
    const quoteLines = quotedText.split('\n').slice(0, 3).join('\n');
    const quote = quoteLines.length < quotedText.length ? quoteLines + '…' : quoteLines;
    const contentToSend = savedReplyingTo
      ? `> ${senderName}: ${quote}\n\n${value}`
      : value;
    const gemmaQuery = extractGemmaQueryForSend(contentToSend, activeConversation.id);
    if (isFieldWorker) {
      if (kzmStagingPdfFiles.length > 0) {
        setIcComposerError('Dostęp do Gemmy nie jest dostępny na koncie pracownika terenowego.');
        return;
      }
      if (gemmaQuery != null) {
        setIcComposerError('Dostęp do Gemmy nie jest dostępny na koncie pracownika terenowego.');
        return;
      }
    }
    if (kzmStagingPdfFiles.length > 0 && gemmaQuery == null) {
      setIcComposerError(
        'Aby wysłać PDF pod KZM, wpisz @gemma w treści wiadomości.',
      );
      return;
    }
    if (gemmaPipelineRef.current) {
      setIcComposerError('Gemma generuje odpowiedź — poczekaj chwilę.');
      return;
    }
    /* Zwykłe wiadomości wysyłamy równolegle (POST jest niezależny, a `postSendSyncSeqRef` serializuje `listMessages`).
     * Blokujemy tylko gdy aktualnie leci ścieżka @gemma — tam user msg + stream SSE muszą iść po kolei. */
    const isGemmaSend = gemmaQuery != null;
    if (isGemmaSend && sendingRef.current) {
      setIcComposerError('Trwa wysyłanie poprzedniej wiadomości — poczekaj chwilę.');
      return;
    }
    setIcComposerError(null);
    sendingRef.current = true;
    setSending(true);
    setReplyingTo(null);
    // Optimistyczne czyszczenie pola i wstawienie bąbelka lokalnie
    if (overrideText === undefined) setText('');
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const optimistic = {
      id: tempId,
      text: contentToSend,
      createTime: new Date().toISOString(),
      sender: { displayName: user?.display_name || user?.email },
      senderId: user?.user_id ? String(user.user_id) : null,
    } as ChatMessage;
    setMessages(prev => [...prev, optimistic]);
    setTimeout(() => { scrollRef.current?.scrollTo({ top: 999999, behavior: 'auto' }); }, 0);

    /* Zwykła wiadomość: puść lock już TU — przed `await sendMessage`, bo optymistyczny bąbelek jest już w UI.
     * Dzięki temu user może słać kolejne krótkie wiadomości bez komunikatu „poczekaj…”, nawet gdy poprzedni POST
     * jeszcze nie wrócił. POST i następny sync robimy fire-and-forget; `postSendSyncSeqRef` chroni kolejność. */
    if (!isGemmaSend) {
      sendingRef.current = false;
      setSending(false);
      const cid = activeConversation.id;
      const seq = ++postSendSyncSeqRef.current;
      const snapshotValueRaw = valueRaw;
      const snapshotReplyingTo = savedReplyingTo;
      void (async () => {
        try {
          const ok = await sendMessage(cid, contentToSend);
          if (!ok) {
            setMessages(prev => prev.filter(m => m.id !== tempId));
            setIcComposerError('Nie udało się wysłać wiadomości. Spróbuj ponownie.');
            /* Nie nadpisuj tekstu, jeśli user zdążył coś już wpisać — ochrona przed utratą nowej treści. */
            setText(prev => (prev.trim() ? prev : snapshotValueRaw));
            setReplyingTo(prev => prev ?? snapshotReplyingTo);
            return;
          }
          if (String(activeConversationIdRef.current || '') !== String(cid)) return;
          const { items } = await listMessages(cid);
          if (seq !== postSendSyncSeqRef.current) return;
          if (String(activeConversationIdRef.current || '') !== String(cid)) return;
          setMessages(items);
          try {
            await markRead(cid, items.length ? String(items[items.length - 1].id) : undefined);
          } catch {
            /* noop */
          }
          setTimeout(() => {
            try {
              scrollRef.current?.scrollTo({ top: 999999, behavior: 'smooth' });
            } catch {
              /* noop */
            }
          }, 0);
        } catch {
          setMessages(prev => prev.filter(m => m.id !== tempId));
          setIcComposerError('Nie udało się wysłać wiadomości. Spróbuj ponownie.');
          setText(prev => (prev.trim() ? prev : snapshotValueRaw));
          setReplyingTo(prev => prev ?? snapshotReplyingTo);
        }
      })();
      return;
    }

    try {
      const ok = await sendMessage(activeConversation.id, contentToSend);
      if (!ok) {
        setMessages(prev => prev.filter(m => m.id !== tempId));
        if (overrideText === undefined) setText(valueRaw);
        setReplyingTo(savedReplyingTo);
        return;
      }

      if (gemmaQuery != null) {
        const { items } = await listMessages(activeConversation.id);
        try { await markRead(activeConversation.id, items.length ? items[items.length - 1].id : undefined); } catch {}
        /* Wiadomość użytkownika jest już na serwerze — zwolnij lock przed streamem SSE; zapis odpowiedzi idzie w tle. */
        sendingRef.current = false;
        setSending(false);
        gemmaPipelineRef.current = true;
        setGemmaThinking(true);
        if (gemmaActivityFlushRafRef.current != null) {
          cancelAnimationFrame(gemmaActivityFlushRafRef.current);
          gemmaActivityFlushRafRef.current = null;
        }
        gemmaActivityPendingRef.current = [];
        setGemmaActivityLines([]);
        setGemmaActivityExpanded(false);
        if (gemmaStreamScrollTimerRef.current != null) {
          clearTimeout(gemmaStreamScrollTimerRef.current);
          gemmaStreamScrollTimerRef.current = null;
        }
        let gemmaPendingId: string | null = null;
        try {
          let model = 'gemma-4-31B-it';
          try {
            model = localStorage.getItem('ai:model:spark') || model;
          } catch {}
          const stickyPid = extractStickyProjectIdFromGemmaTurns(activeConversation.id);
          const gemmaQueryForApi =
            stickyPid != null && stickyPid.length > 0
              ? `${gemmaQuery}\n\n[System / backend: W tej rozmowie wcześniej ustalono **project_id=${stickyPid}** (z odpowiedzi Gemmy). Przy wywołaniu \`kzm_quick_create\` użyj **dokładnie tego** \`project_id\`, dopóki użytkownik wyraźnie nie wskaże innego projektu — nie zamieniaj na inny identyfikator.]`
              : gemmaQuery;
          const payload = [...loadGemmaTurnsForApi(activeConversation.id), { role: 'user' as const, content: gemmaQueryForApi }];
          gemmaPendingId = `gemma-pending-${Date.now()}`;
          setGemmaLiveStreamMessageId(gemmaPendingId);
          const pendingBubble = {
            id: gemmaPendingId,
            text: '',
            createTime: new Date().toISOString(),
            sender: { displayName: 'Gemma', avatarUrl: GEMMA_ICON_URL },
            senderId: 'gemma-local',
          } as ChatMessage;
          /* Dwukrokowo jak przed commitem „sesja 30 min”: najpierw sync z serwera, potem dopięcie pending —
           * unika potencjalnych problemów z batchowaniem / kolejnością względem innych aktualizacji stanu. */
          setMessages(items);
          setMessages((prev) => [...prev, pendingBubble]);
          gemmaDeltaAccumRef.current = '';
          if (gemmaDeltaRafRef.current != null) {
            cancelAnimationFrame(gemmaDeltaRafRef.current);
            gemmaDeltaRafRef.current = null;
          }
          gemmaDeltaTargetIdRef.current = gemmaPendingId;
          setTimeout(() => {
            scrollRef.current?.scrollTo({ top: 999999, behavior: 'smooth' });
          }, 0);

          const flushGemmaDeltaToUi = () => {
            gemmaDeltaRafRef.current = null;
            const id = gemmaDeltaTargetIdRef.current;
            if (!id) return;
            const t = gemmaDeltaAccumRef.current;
            const el = gemmaStreamTextElRef.current;
            if (el) el.textContent = t;
            if (gemmaStreamScrollTimerRef.current == null) {
              gemmaStreamScrollTimerRef.current = setTimeout(() => {
                gemmaStreamScrollTimerRef.current = null;
                try {
                  scrollRef.current?.scrollTo({ top: 999999, behavior: 'auto' });
                } catch {
                  /* noop */
                }
              }, 80);
            }
          };

          const onStream = (ev: AgentMysqlStreamEvent) => {
            const pid = gemmaPendingId;
            if (ev.type === 'assistant_delta' && pid && ev.text) {
              gemmaDeltaAccumRef.current += ev.text;
              if (gemmaDeltaRafRef.current == null) {
                gemmaDeltaRafRef.current = requestAnimationFrame(flushGemmaDeltaToUi);
              }
              return;
            }
            if (ev.type === 'assistant_stream_reset' && pid) {
              if (gemmaDeltaRafRef.current != null) {
                cancelAnimationFrame(gemmaDeltaRafRef.current);
                gemmaDeltaRafRef.current = null;
              }
              gemmaDeltaAccumRef.current = '';
              if (gemmaStreamTextElRef.current) gemmaStreamTextElRef.current.textContent = '';
              setMessages((prev) => prev.map((m) => (m.id === pid ? { ...m, text: '' } : m)));
              return;
            }
            if (ev.type === 'started') pushGemmaActivityLine('Start', `Do ${ev.maxIterations} rund z narzędziami`);
            if (ev.type === 'thinking') pushGemmaActivityLine('Thinking', `Runda ${ev.iteration} / ${ev.maxIterations} — model`);
            if (ev.type === 'tool_calls') pushGemmaActivityLine('Tool', ev.tools.join(', '));
            if (ev.type === 'mysql_read_query') pushGemmaActivityLine('Read mysql_read_query', ev.preview);
            if (ev.type === 'mysql_read_query_done') {
              pushGemmaActivityLine(ev.ok ? 'SQL — wykonano' : 'SQL — błąd', ev.message);
            }
            if (ev.type === 'read_context_file') pushGemmaActivityLine('read_context_file', ev.preview || ev.path);
            if (ev.type === 'read_context_file_done') {
              pushGemmaActivityLine(
                ev.ok ? 'Plik kontekstu — wczytano' : 'Plik kontekstu — błąd',
                ev.ok ? ev.path || '—' : ev.message,
              );
            }
            if (ev.type === 'kzm_quick_create') pushGemmaActivityLine('kzm_quick_create', ev.preview);
            if (ev.type === 'kzm_quick_create_done') {
              pushGemmaActivityLine(
                ev.ok ? 'KZM — utworzono' : 'KZM — błąd',
                ev.ok
                  ? `HTTP ${ev.httpStatus ?? '—'}`
                  : `${ev.httpStatus != null ? `HTTP ${ev.httpStatus}: ` : ''}${ev.message || '—'}`,
              );
            }
            if (ev.type === 'kzm_staging_upload') pushGemmaActivityLine('Staging PDF', `${ev.count} plik(ów)`);
            if (ev.type === 'kzm_staging_upload_done') {
              pushGemmaActivityLine(ev.ok ? 'Staging — OK' : 'Staging — błąd', ev.message);
            }
            if (ev.type === 'unknown_tool') pushGemmaActivityLine('Nieznane narzędzie', ev.name || '—');
            if (ev.type === 'error') pushGemmaActivityLine('Błąd', ev.message);
          };
          const pdfsForKzm = [...kzmStagingPdfFiles];
          const reply =
            pdfsForKzm.length > 0
              ? await agentChatStreamWithFiles(payload, pdfsForKzm, { max_tokens: 4096, model }, onStream)
              : await agentChatStream(payload, { max_tokens: 4096, model }, onStream);
          if (gemmaDeltaRafRef.current != null) {
            cancelAnimationFrame(gemmaDeltaRafRef.current);
            gemmaDeltaRafRef.current = null;
          }
          if (gemmaActivityFlushRafRef.current != null) {
            cancelAnimationFrame(gemmaActivityFlushRafRef.current);
            gemmaActivityFlushRafRef.current = null;
          }
          flushPendingGemmaActivitySync();
          if (gemmaStreamScrollTimerRef.current != null) {
            clearTimeout(gemmaStreamScrollTimerRef.current);
            gemmaStreamScrollTimerRef.current = null;
          }
          try {
            scrollRef.current?.scrollTo({ top: 999999, behavior: 'auto' });
          } catch {
            /* noop */
          }
          gemmaDeltaAccumRef.current = '';
          gemmaDeltaTargetIdRef.current = null;
          if (gemmaStreamTextElRef.current) gemmaStreamTextElRef.current.textContent = '';
          setGemmaLiveStreamMessageId(null);
          setMessages((prev) =>
            prev.map((m) => (m.id === gemmaPendingId ? { ...m, text: reply } : m)),
          );
          /* Stream skończony — sendingRef już zwolniony przed streamem; tu tylko UI aktywności. */
          setGemmaActivityLines([]);
          setGemmaThinking(false);
          setKzmStagingPdfFiles([]);
          appendGemmaExchange(activeConversation.id, gemmaQuery, reply);
          setIcComposerError(null);
          const convId = activeConversation.id;
          const pendingIdForPersist = gemmaPendingId;
          const replyForPersist = reply;
          void (async () => {
            try {
              const { persisted, message: savedFromPost } = await postGemmaAssistantMessage(convId, replyForPersist);
              if (!persisted) {
                setGemmaSaveWarn(
                  'Odpowiedź Gemmy nie została zapisana w czacie (serwer: INTERNAL_CHAT_GEMMA_BOT_USER_ID i konto bota w tabeli users). Po odświeżeniu zniknie — skonfiguruj backend.',
                );
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === pendingIdForPersist
                      ? ({
                          ...m,
                          id: `gemma-local-${Date.now()}`,
                          senderId: 'gemma-local',
                        } as ChatMessage)
                      : m,
                  ),
                );
              } else {
                const sync = await listMessages(convId);
                let lastReadId: string | undefined;
                setMessages((prev) => {
                  const merged = mergeGemmaReplyWithServerList(
                    prev,
                    sync.items,
                    pendingIdForPersist!,
                    replyForPersist,
                    savedFromPost,
                  );
                  lastReadId = merged.length ? String(merged[merged.length - 1].id) : undefined;
                  return merged;
                });
                try {
                  await markRead(convId, lastReadId);
                } catch {}
              }
            } catch {
              setGemmaSaveWarn('Nie udało się zapisać odpowiedzi Gemmy w czacie. Spróbuj odświeżyć wątek.');
            }
          })();
          setTimeout(() => { scrollRef.current?.scrollTo({ top: 999999, behavior: 'smooth' }); }, 0);
        } catch (gemmaErr: unknown) {
          if (gemmaDeltaRafRef.current != null) {
            cancelAnimationFrame(gemmaDeltaRafRef.current);
            gemmaDeltaRafRef.current = null;
          }
          if (gemmaStreamScrollTimerRef.current != null) {
            clearTimeout(gemmaStreamScrollTimerRef.current);
            gemmaStreamScrollTimerRef.current = null;
          }
          gemmaDeltaAccumRef.current = '';
          gemmaDeltaTargetIdRef.current = null;
          if (gemmaStreamTextElRef.current) gemmaStreamTextElRef.current.textContent = '';
          setGemmaLiveStreamMessageId(null);
          if (gemmaPendingId) {
            setMessages((prev) => prev.filter((m) => m.id !== gemmaPendingId));
          }
          const msg =
            (gemmaErr as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            (gemmaErr as Error)?.message ||
            'Błąd Gemmy';
          setMessages((prev) => [
            ...prev,
            {
              id: `gemma-err-${Date.now()}`,
              text: `Nie udało się uzyskać odpowiedzi Gemmy: ${msg}`,
              createTime: new Date().toISOString(),
              sender: { displayName: 'Gemma', avatarUrl: GEMMA_ICON_URL },
              senderId: 'gemma-local',
            } as ChatMessage,
          ]);
          setTimeout(() => { scrollRef.current?.scrollTo({ top: 999999, behavior: 'smooth' }); }, 0);
        } finally {
          gemmaPipelineRef.current = false;
          setIcComposerError(null);
          setGemmaLiveStreamMessageId(null);
          if (gemmaActivityFlushRafRef.current != null) {
            cancelAnimationFrame(gemmaActivityFlushRafRef.current);
            gemmaActivityFlushRafRef.current = null;
          }
          flushPendingGemmaActivitySync();
          setGemmaThinking(false);
          setGemmaActivityLines([]);
        }
      }
    } catch {
      setMessages(prev => prev.filter(m => m.id !== tempId));
      if (overrideText === undefined) setText(valueRaw);
      setReplyingTo(savedReplyingTo);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  /** Kliknięcie pigułki pod bąbelkiem Gemmy → wyślij jako nową wiadomość z prefiksem `@gemma`, bez ruszania pola composera. */
  const handlePickGemmaInlineOption = React.useCallback(
    (value: string) => {
      if (isFieldWorker) return;
      if (!activeConversation) return;
      if (sending || gemmaThinking) return;
      const trimmed = String(value || '').trim();
      if (!trimmed) return;
      const prefixed = /@gemma\b/i.test(trimmed) ? trimmed : `@gemma ${trimmed}`;
      void handleSend(prefixed);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeConversation, sending, gemmaThinking, isFieldWorker],
  );

  function renderReadCheck(isSelf: boolean, msgId: string) {
    if (!isSelf) return null;
    if (activeConversation?.type !== 'direct') return null;
    const read = readUpToOther && Number(readUpToOther) >= Number(msgId);
    return <DoneAllIcon sx={{ fontSize: 16, ml: 0.5, color: read ? '#1976d2' : 'action.disabled' }} />;
  }

  async function handleSaveEdit() {
    const content = (editInputRef.current?.value ?? editingText).trim();
    if (!editingMessageId || !content) return;
    try {
      const ok = await editMessage(editingMessageId, content);
      if (ok) {
        setMessages(prev => prev.map(m => m.id === editingMessageId ? { ...m, text: content } : m));
        setEditingMessageId(null);
        setEditingText('');
      }
    } catch {}
  }

  function toggleReaction(message: ChatMessage, r: ReactionType) {
    const isSame = message.myReaction === r;
    const messageId = message.id;
    setMessages(prev => prev.map(m => {
      if (m.id !== messageId) return m;
      const counts = { up: m.reactions?.up || 0, down: m.reactions?.down || 0, smile: m.reactions?.smile || 0, heart: m.reactions?.heart || 0, haha: m.reactions?.haha || 0, exclamation: m.reactions?.exclamation || 0, question: m.reactions?.question || 0 };
      if (isSame) {
        if (m.myReaction) counts[m.myReaction] = Math.max(0, counts[m.myReaction] - 1);
        return { ...m, reactions: counts, myReaction: null };
      } else {
        if (m.myReaction) counts[m.myReaction] = Math.max(0, counts[m.myReaction] - 1);
        counts[r] = (counts[r] || 0) + 1;
        return { ...m, reactions: counts, myReaction: r };
      }
    }));
    if (isSame) {
      unreactMessage(messageId).catch(()=>{});
    } else {
      reactMessage(messageId, r).catch(()=>{});
    }
  }

  const REACTION_OPTIONS: { type: ReactionType; emoji: string; label: string }[] = [
    { type: 'heart', emoji: '❤️', label: 'Serce' },
    { type: 'up', emoji: '👍', label: 'Kciuk w górę' },
    { type: 'down', emoji: '👎', label: 'Kciuk w dół' },
    { type: 'haha', emoji: '😂', label: 'Ha Ha' },
    { type: 'exclamation', emoji: '❗', label: 'Wykrzyknik' },
    { type: 'question', emoji: '❓', label: 'Znak zapytania' },
  ];

  function emojiFor(r: ReactionType): string {
    const o = REACTION_OPTIONS.find(x => x.type === r);
    return o?.emoji ?? '🙂';
  }

  function totalReactions(m: ChatMessage): number {
    const r = (m.reactions || {}) as Record<string, number>;
    return (r.up || 0) + (r.down || 0) + (r.smile || 0) + (r.heart || 0) + (r.haha || 0) + (r.exclamation || 0) + (r.question || 0);
  }

  function renderReactionBadge(m: ChatMessage, isSelf: boolean) {
    const r = (m.reactions || {}) as Record<string, number>;
    const hasAny = totalReactions(m) > 0;
    if (!hasAny) return null;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          bgcolor: '#fff',
          borderRadius: '12px',
          px: 0.5,
          py: 0.25,
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          alignSelf: isSelf ? 'flex-end' : 'flex-start',
        }}
      >
        {REACTION_OPTIONS.map(o => (r[o.type] || 0) > 0 && (
          <Typography key={o.type} component="span" sx={{ fontSize: 14, lineHeight: 1 }}>{o.emoji}{(r[o.type] || 0) > 1 ? r[o.type] : ''}</Typography>
        ))}
      </Box>
    );
  }

  function formatFileSize(bytes?: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  async function handleUploadFile(file: File) {
    if (!activeConversation) return;
    setUploadingFile(true);
    setUploadFileName(file.name);
    try {
      await uploadFile(activeConversation.id, file);
      const r = await listMessages(activeConversation.id);
      setMessages(r.items);
      setTimeout(() => { scrollRef.current?.scrollTo({ top: 999999 }); }, 0);
    } catch {}
    setUploadingFile(false);
    setUploadFileName(null);
  }

  function onPaste(e: React.ClipboardEvent<HTMLDivElement>) {
    try {
      const items = e.clipboardData?.items || [];
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.kind === 'file') {
          const f = it.getAsFile();
          if (f) {
            e.preventDefault();
            handleUploadFile(f);
            return;
          }
        }
      }
    } catch {}
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    try {
      const files = Array.from(e.dataTransfer.files || []);
      const file = files[0];
      if (file && activeConversation) {
        handleUploadFile(file);
      }
    } catch {}
  }

  // Reconciliation: co 60s dociągnij brakujące od ostatniego id (na wypadek utraconego eventu)
  useEffect(() => {
    if (!activeConversation) return;
    let interval: any;
    interval = setInterval(async () => {
      try {
        const lastId = lastNumericMessageId(messages);
        if (!lastId) return;
        const more = await listMessagesAfter(activeConversation.id, lastId);
        if (more && more.length) {
          let lastNew: string | undefined;
          setMessages((prev) => {
            const existing = new Set(prev.map((m) => String(m.id)));
            const fresh = more.filter((m) => !existing.has(String(m.id)));
            if (fresh.length === 0) return prev;
            lastNew = String(fresh[fresh.length - 1].id);
            return [...prev, ...fresh];
          });
          setTimeout(() => { scrollRef.current?.scrollTo({ top: 999999, behavior: 'auto' }); }, 0);
          if (lastNew) try { await markRead(activeConversation.id, lastNew); } catch {}
        }
      } catch {}
    }, 60_000);
    return () => { if (interval) clearInterval(interval); };
  }, [activeConversation?.id, messages.length]);

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}
      onPaste={onPaste}
      onDrop={onDrop}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={(e) => { if (e.currentTarget.contains(e.relatedTarget as Node)) return; setDragOver(false); }}
    >
      {showHeader && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {activeConversation?.type === 'group' ? <ForumIcon color="primary" /> : <PersonOutlineIcon color="primary" />}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
          </Box>
          <Divider />
        </>
      )}
      {dragOver && (
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 10,
          bgcolor: 'rgba(63,63,220,0.08)',
          border: '2px dashed #3F3FDC',
          borderRadius: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none'
        }}>
          <Typography variant="subtitle1" sx={{ color: '#3F3FDC', fontWeight: 600 }}>Upuść plik tutaj</Typography>
        </Box>
      )}
      <Box ref={scrollRef} sx={{ flex: 1, overflow: 'auto', py: 1, px: 2 }}>
        {!!nextPageToken && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <Button size="small" variant="outlined" onClick={loadOlder}>Załaduj starsze</Button>
          </Box>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><CircularProgress size={24} /></Box>
        ) : (
          <Box component="div" sx={{ width: '100%' }}>
          <List dense>
            {(() => {
              let lastGemmaMessageId: string | null = null;
              for (let i = messages.length - 1; i >= 0; i--) {
                if (isGemmaMessage(messages[i])) {
                  lastGemmaMessageId = String(messages[i].id);
                  break;
                }
              }
              return messages.map(m => {
              const isSelf = user?.user_id && m.senderId ? String(user.user_id) === String(m.senderId) : false;
              const bubbleBg = isSelf
                ? '#E6F5E6'
                : isGemmaMessage(m)
                  ? INTERNAL_CHAT_GEMMA_BUBBLE_BG
                  : '#FFF5CC';
              const textColor = '#020242';
              const showTextBubble =
                Boolean(m.text) ||
                (gemmaThinking &&
                  gemmaLiveStreamMessageId === String(m.id) &&
                  String(m.id).startsWith('gemma-pending-'));
              return (
                <ListItem key={m.id} alignItems="flex-start" sx={{ px: 0, py: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      px: 0.5,
                      py: 0.75,
                      mb: 0.5,
                      borderRadius: 1,
                      alignItems: isSelf ? 'flex-end' : 'flex-start'
                    }}
                  >
                    <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          maxWidth: '100%',
                          width: 'auto',
                          '&:hover [data-msg-actions]': { opacity: 1 },
                        }}
                      >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: isSelf ? 'flex-end' : 'flex-start', flexDirection: 'row', whiteSpace: 'nowrap', minWidth: 0 }}>
                        {isGemmaMessage(m) ? (
                          <GemmaAvatar size={20} />
                        ) : (
                          <Avatar src={resolveGemmaMessageAvatarUrl(m) || undefined} sx={{ width: 20, height: 20 }}>
                            {(m.sender?.displayName || m.sender?.email || '?').slice(0, 1).toUpperCase()}
                          </Avatar>
                        )}
                        <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {m.sender?.displayName || m.sender?.email || 'Użytkownik'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                          {formatYmdHms(m.createTime)}
                          {renderReadCheck(isSelf, m.id)}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', alignItems: isSelf ? 'flex-end' : 'flex-start', gap: 0.5 }}>
                        {renderReactionBadge(m, isSelf)}
                        {openReactionsForId === String(m.id) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {REACTION_OPTIONS.map(o => (
                              <Tooltip key={o.type} title={o.label} arrow>
                                <Box
                                  role="button"
                                  tabIndex={0}
                                  sx={{ cursor: 'pointer', fontSize: 20, lineHeight: 1, p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' } }}
                                  onClick={() => { toggleReaction(m, o.type); setOpenReactionsForId(null); }}
                                >
                                  {o.emoji}
                                </Box>
                              </Tooltip>
                            ))}
                          </Box>
                        )}
                      </Box>
                      {showTextBubble && (() => {
                        const isEditing = editingMessageId === String(m.id);
                        if (isEditing) {
                          return (
                            <Box sx={{ mt: 0.25, display: 'inline-flex', flexDirection: 'column', gap: 0.75, alignSelf: isSelf ? 'flex-end' : 'flex-start', minWidth: 200, maxWidth: { xs: '85%', sm: '75%' } }}>
                              <InputBase
                                inputRef={editInputRef}
                                value={editingText}
                                onChange={(e) => setEditingText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Escape') { setEditingMessageId(null); setEditingText(''); }
                                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(); }
                                }}
                                multiline
                                minRows={2}
                                maxRows={8}
                                sx={{
                                  bgcolor: bubbleBg,
                                  px: 1.25,
                                  py: 0.9,
                                  borderRadius: '4px',
                                  fontSize: 14,
                                  border: '1px solid #3F3FDC',
                                }}
                                autoFocus
                              />
                              <Box sx={{ display: 'flex', gap: 0.5, alignSelf: 'flex-end' }}>
                                <Tooltip title="Anuluj" arrow>
                                  <IconButton size="small" onClick={() => { setEditingMessageId(null); setEditingText(''); }} sx={{ color: '#595966' }}>
                                    <CloseIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Zapisz" arrow>
                                  <IconButton size="small" onClick={handleSaveEdit} disabled={!editingText.trim()} sx={{ color: '#3F3FDC' }}>
                                    <CheckIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          );
                        }
                        const isLiveGemmaStream =
                          gemmaThinking &&
                          gemmaLiveStreamMessageId === String(m.id) &&
                          String(m.id).startsWith('gemma-pending-');
                        if (isLiveGemmaStream) {
                          return (
                            <Box
                              sx={{
                                mt: 0.25,
                                display: 'inline-flex',
                                flexDirection: 'column',
                                gap: 0.75,
                                alignSelf: isSelf ? 'flex-end' : 'flex-start',
                                maxWidth: { xs: '85%', sm: '75%' },
                              }}
                            >
                              <Box
                                sx={{
                                  bgcolor: bubbleBg,
                                  color: textColor,
                                  px: 1.25,
                                  py: 0.9,
                                  borderRadius: '4px',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                                }}
                              >
                                <Typography
                                  component="div"
                                  variant="body2"
                                  sx={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                  }}
                                >
                                  <span ref={gemmaStreamTextElRef} />
                                </Typography>
                              </Box>
                            </Box>
                          );
                        }
                        const boardParsed = parseBoardCommentMessage(m.text);
                        const parsed = !boardParsed ? parseReplyMessage(m.text) : null;
                        return (
                          <Box sx={{ mt: 0.25, display: 'inline-flex', flexDirection: 'column', gap: 0.75, alignSelf: isSelf ? 'flex-end' : 'flex-start', maxWidth: { xs: '85%', sm: '75%' } }}>
                            {boardParsed ? (
                              <Box sx={{ bgcolor: bubbleBg, color: textColor, px: 1.25, py: 0.9, borderRadius: '4px', boxShadow: '0 1px 2px rgba(0,0,0,0.06)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                                <InternalChatFormattedHtml text={boardParsed.prefix} preset="boardInline" component="span" />
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => {
                                    (() => {
                                      const url = getBoardUrl(boardParsed.projectId);
                                      const sep = url.includes('?') ? '&' : '?';
                                      window.open(`${url}${sep}comment=${encodeURIComponent(boardParsed.commentId)}`, '_blank');
                                    })();
                                  }}
                                  sx={{ flexShrink: 0, textTransform: 'none' }}
                                >
                                  Przejdź
                                </Button>
                              </Box>
                            ) : parsed ? (
                              <>
                                <Box
                                  sx={{
                                    bgcolor: 'rgba(0,0,0,0.06)',
                                    borderLeft: '3px solid #3F3FDC',
                                    px: 1,
                                    py: 0.75,
                                    borderRadius: '0 4px 4px 0',
                                    alignSelf: 'stretch',
                                  }}
                                >
                                  <Typography variant="caption" sx={{ color: '#3F3FDC', fontWeight: 600, fontSize: 12 }}>
                                    {parsed.quoteAuthor}
                                  </Typography>
                                  <InternalChatFormattedHtml text={parsed.quoteText} preset="replyQuote" />
                                </Box>
                                <Box sx={{ bgcolor: bubbleBg, color: textColor, px: 1.25, py: 0.9, borderRadius: '4px', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
                                  {isGemmaMessage(m) ? (
                                    <GemmaBubbleWithKzmPdfButton
                                      text={parsed.replyText}
                                      preset="replyBody"
                                      onPickKzmPdf={isFieldWorker ? (() => {}) : pickKzmPdfForGemma}
                                      pickDisabled={isFieldWorker || sending || gemmaThinking}
                                      isLastGemmaBubble={lastGemmaMessageId !== null && String(m.id) === lastGemmaMessageId}
                                      onPickOption={isFieldWorker ? undefined : handlePickGemmaInlineOption}
                                      pickOptionsDisabled={sending || gemmaThinking}
                                    />
                                  ) : (
                                    <InternalChatFormattedHtml text={parsed.replyText} preset="replyBody" />
                                  )}
                                </Box>
                              </>
                            ) : (
                              <Box sx={{ bgcolor: bubbleBg, color: textColor, px: 1.25, py: 0.9, borderRadius: '4px', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
                                {isGemmaMessage(m) ? (
                                  <GemmaBubbleWithKzmPdfButton
                                    text={m.text || ''}
                                    preset="bubble"
                                    onPickKzmPdf={isFieldWorker ? (() => {}) : pickKzmPdfForGemma}
                                    pickDisabled={isFieldWorker || sending || gemmaThinking}
                                    isLastGemmaBubble={lastGemmaMessageId !== null && String(m.id) === lastGemmaMessageId}
                                    onPickOption={isFieldWorker ? undefined : handlePickGemmaInlineOption}
                                    pickOptionsDisabled={sending || gemmaThinking}
                                  />
                                ) : (
                                  <InternalChatFormattedHtml text={m.text || ''} preset="bubble" />
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })()}
                      {!m.text && m.imageUrl && (
                        <Box sx={{ mt: 0.5, alignSelf: isSelf ? 'flex-end' : 'flex-start' }}>
                          <Box
                            component="img"
                            src={m.imageUrl}
                            alt="Załącznik"
                            onClick={()=>setPreviewUrl(m.imageUrl!)}
                            sx={{ maxWidth: 260, maxHeight: 200, width: 'auto', height: 'auto', borderRadius: 2, boxShadow: '0 1px 2px rgba(0,0,0,0.10)', cursor: 'zoom-in' }}
                          />
                        </Box>
                      )}
                      {!m.text && !m.imageUrl && m.fileUrl && (() => {
                        const isPdf = (m.fileName || '').toLowerCase().endsWith('.pdf');
                        return (
                          <Box
                            sx={{
                              mt: 0.5,
                              alignSelf: isSelf ? 'flex-end' : 'flex-start',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              bgcolor: bubbleBg,
                              px: 1.5,
                              py: 1,
                              borderRadius: '6px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                              maxWidth: { xs: '85%', sm: '75%' },
                            }}
                          >
                            {isPdf ? (
                              <Box component="img" src={PdfIconSvg} alt="PDF" sx={{ width: 28, height: 28, flexShrink: 0 }} />
                            ) : (
                              <InsertDriveFileOutlinedIcon sx={{ fontSize: 28, color: '#3F3FDC', flexShrink: 0 }} />
                            )}
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                component="a"
                                href={m.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="body2"
                                sx={{ fontWeight: 500, color: '#020242', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                              >
                                {m.fileName || 'Plik'}
                              </Typography>
                              {m.fileSize != null && (
                                <Typography variant="caption" color="text.secondary">{formatFileSize(m.fileSize)}</Typography>
                              )}
                            </Box>
                            <Tooltip title="Pobierz" arrow>
                              <IconButton
                                size="small"
                                onClick={async () => {
                                  try {
                                    const resp = await fetch(m.fileUrl!);
                                    const blob = await resp.blob();
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = m.fileName || 'plik';
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                    window.URL.revokeObjectURL(url);
                                  } catch {}
                                }}
                                sx={{ flexShrink: 0, color: '#3F3FDC', '&:hover': { bgcolor: 'rgba(63,63,220,0.08)' } }}
                              >
                                <DownloadIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        );
                      })()}
                      <Box
                        data-msg-actions
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mt: 0.25,
                          alignSelf: isSelf ? 'flex-end' : 'flex-start',
                          opacity: openReactionsForId === String(m.id) ? 1 : 0,
                          transition: 'opacity 0.15s ease',
                        }}
                      >
                        <Tooltip title="Odpowiedz na tę wiadomość" arrow>
                          <IconButton
                            size="small"
                            onClick={(e)=>{ e.stopPropagation(); setOpenReactionsForId(null); setReplyingTo(m); setTimeout(() => textInputRef.current?.focus(), 0); }}
                            sx={{ p: 0.25, color: 'action.active' }}
                          >
                            <Box component="img" src="/assets/icons/reply.svg" alt="Odpowiedz" sx={{ width: 18, height: 18, display: 'block' }} />
                          </IconButton>
                        </Tooltip>
                        {isSelf && m.text && editingMessageId !== String(m.id) && (
                          <Tooltip title="Edytuj wiadomość" arrow>
                            <IconButton
                              size="small"
                              onClick={(e)=>{ e.stopPropagation(); setOpenReactionsForId(null); setEditingMessageId(String(m.id)); setEditingText(m.text); }}
                              sx={{ p: 0.25, color: 'action.active' }}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!isSelf && (
                          <IconButton
                            size="small"
                            onClick={(e)=> { e.stopPropagation(); setOpenReactionsForId(prev => prev === String(m.id) ? null : String(m.id)); }}
                            sx={{ p: 0.25, color: 'action.active' }}
                            title="Dodaj reakcję"
                          >
                            {customAddIconOk ? (
                              <Box
                                component="img"
                                src="/assets/icons/add_reaction.svg"
                                alt="Add reaction"
                                sx={{ width: 18, height: 18, display: 'block' }}
                                onError={() => setCustomAddIconOk(false)}
                              />
                            ) : (
                              <AddReactionIcon sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              );
              });
            })()}
          </List>
          {gemmaThinking && (
            <Box sx={{ mt: 0.5 }}>
              {gemmaActivityLines.length > 0 && (
                <GemmaAgentActivityPanel
                  lines={gemmaActivityLines}
                  expanded={gemmaActivityExpanded}
                  onToggle={() => setGemmaActivityExpanded((v) => !v)}
                />
              )}
              <LinearProgress sx={{ mt: gemmaActivityLines.length > 0 ? 0.75 : 0 }} />
            </Box>
          )}
          <Box ref={messagesEndRef} aria-hidden sx={{ height: 0, width: '100%', overflow: 'hidden' }} />
          </Box>
        )}
      </Box>
      
      {uploadingFile && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: '#F5F5FF', borderTop: '1px solid #ededed' }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Przesyłanie{uploadFileName ? `: ${uploadFileName}` : '…'}
          </Typography>
        </Box>
      )}
      <Divider />
      <Box sx={{ px: 2, pt: 0, pb: 1, mt: 1 }}>
        {replyingTo && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              mb: 1,
              p: 1.25,
              bgcolor: '#E8E8FF',
              borderRadius: '8px',
              border: '1px solid #3F3FDC',
              boxShadow: '0 1px 3px rgba(63,63,220,0.15)',
            }}
          >
            <Box component="img" src="/assets/icons/reply.svg" alt="" sx={{ width: 20, height: 20, mt: 0.25, flexShrink: 0, filter: 'invert(24%) sepia(89%) saturate(2000%) hue-rotate(230deg)' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: '#3F3FDC', fontWeight: 700, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>
                Odpowiedź na wiadomość
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                {isGemmaMessage(replyingTo) ? (
                  <GemmaAvatar size={24} sx={{ flexShrink: 0 }} />
                ) : (
                  <Avatar src={resolveGemmaMessageAvatarUrl(replyingTo) || undefined} sx={{ width: 24, height: 24, flexShrink: 0 }}>
                    {(replyingTo.sender?.displayName || replyingTo.sender?.email || '?').slice(0, 1).toUpperCase()}
                  </Avatar>
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2D2D32', fontSize: 14 }}>
                    {replyingTo.sender?.displayName || replyingTo.sender?.email || 'Użytkownik'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#595966',
                      fontSize: 13,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {replyingTo.text || replyingTo.fileName || '[załącznik]'}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setReplyingTo(null)} sx={{ color: '#595966', p: 0.25 }} title="Anuluj odpowiedź">
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        )}
        {icComposerError && (
          <Alert severity="warning" sx={{ mb: 1, py: 0.5 }} onClose={() => setIcComposerError(null)}>
            {icComposerError}
          </Alert>
        )}
        {kzmStagingPdfFiles.length > 0 && !isFieldWorker && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              PDF pod KZM (wyślij z @gemma lub w sesji Gemmy)
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {kzmStagingPdfFiles.map((f, i) => (
                <Chip
                  key={`kzm-ic-${i}-${f.name}`}
                  variant="outlined"
                  size="small"
                  label={f.name}
                  onDelete={() => setKzmStagingPdfFiles((prev) => prev.filter((_, j) => j !== i))}
                />
              ))}
            </Box>
          </Box>
        )}
        <input
          ref={kzmGemmaPdfInputRef}
          type="file"
          hidden
          multiple
          accept="application/pdf,.pdf"
          onChange={(e) => {
            const fl = Array.from(e.target.files || []).filter(
              (f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'),
            );
            setKzmStagingPdfFiles((prev) => [...prev, ...fl].slice(0, MAX_KZM_IC_PDF));
            e.target.value = '';
          }}
        />
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleUploadFile(f);
            e.target.value = '';
          }}
        />
        <Paper
          elevation={0}
          sx={{
            px: 0,
            py: 0,
            display: 'flex',
            alignItems: 'flex-start',
            borderRadius: '4px',
            backgroundColor: '#F5F5FF',
            border: '1px solid #ededed',
            height: 'auto',
            minHeight: 72,
            width: '100%',
            /* overflow:hidden przycina warstwę highlightera react-mentions — brak podświetlenia / „zeskoku” tekstu */
            overflow: 'visible',
            '&:hover': { borderColor: '#B9B9B9' }
          }}
        >
          <Tooltip title="Dodaj plik do czatu" arrow>
            <Box
              onClick={() => { if (activeConversation && !uploadingFile) fileInputRef.current?.click(); }}
              sx={{
                ml: 0.5,
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: activeConversation && !uploadingFile ? '#6B6BD6' : '#B9B9B9',
                cursor: activeConversation && !uploadingFile ? 'pointer' : 'default',
                userSelect: 'none',
                alignSelf: 'center',
                borderRadius: '50%',
                '&:hover': activeConversation && !uploadingFile ? { bgcolor: 'rgba(63,63,220,0.08)' } : {}
              }}
            >
              <AttachFileIcon sx={{ fontSize: 20, transform: 'rotate(45deg)' }} />
            </Box>
          </Tooltip>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              pl: 1,
              pr: 1,
              pt: 1,
              pb: 1,
              position: 'relative',
              overflow: 'visible',
              '& .mentions__control': { overflow: 'visible' },
              '& .internal-chat-mentions': { position: 'relative', zIndex: 2 },
            }}
          >
            {activeConversation && gemmaTypingHint && (
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  /* content box rodzica + offset w pionie/poziomie (fine-tuning). */
                  top: (t) => `calc(${t.spacing(2)} - 8px)`,
                  right: 1,
                  bottom: 1,
                  left: (t) => `calc(${t.spacing(2)} - 7px)`,
                  boxSizing: 'border-box',
                  padding: ICC_COMPOSER_INPUT_PADDING,
                  pointerEvents: 'none',
                  overflow: 'hidden',
                  zIndex: 1,
                }}
              >
                <span
                  style={{
                    display: 'block',
                    transform: `translate(-${gemmaGhostScroll.left}px, -${gemmaGhostScroll.top}px)`,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: ICC_COMPOSER_FONT_PX,
                    lineHeight: `${ICC_COMPOSER_LINE_HEIGHT_PX}px`,
                    fontFamily: ICC_COMPOSER_FONT_STACK,
                    fontWeight: 400,
                    WebkitFontSmoothing: 'antialiased',
                  }}
                >
                  <span style={{ color: '#F5F5FF' }}>{internalChatComposerPlainText(text)}</span>
                  <span style={{ color: 'rgba(89, 89, 102, 0.48)' }}>{gemmaTypingHint.suffix}</span>
                </span>
              </Box>
            )}
            <MentionsInput
              key={activeConversation?.id ?? 'no-conversation'}
              inputRef={textInputRef}
              value={text}
              onChange={(_e, newValue) => {
                setIcComposerError(null);
                setText(newValue);
                // react-mentions odświeża listę głównie w onSelect; przy zwykłym pisaniu zdarzenie select często nie leci,
                // więc po @, @g itd. nie było podpowiedzi. Sztuczne „select” po commit wartości — updateMentionsQueries widzi aktualne props.value.
                window.setTimeout(() => {
                  const ta = textInputRef.current;
                  if (!ta || document.activeElement !== ta) return;
                  ta.dispatchEvent(new Event('select', { bubbles: true }));
                }, 0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !(e.nativeEvent as KeyboardEvent).isComposing) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={activeConversation ? (isFieldWorker ? 'Wiadomość…' : 'Wiadomość… @ lub @g — podpowiedź Gemmy, Tab / Enter wstawia @gemma') : 'Wybierz rozmowę'}
              disabled={!activeConversation}
              singleLine={false}
              allowSuggestionsAboveCursor
              forceSuggestionsAboveCursor
              ignoreAccents
              suggestionsPortalHost={typeof document !== 'undefined' ? document.body : undefined}
              a11ySuggestionsListLabel="Sugestie wzmianek"
              style={internalChatMentionsStyles}
              className="internal-chat-mentions"
            >
              <Mention
                trigger="@"
                data={mentionData}
                markup="@__display__"
                regex={GEMMA_MENTION_MARKUP_REGEX}
                appendSpaceOnAdd
                displayTransform={(_id: string | number, display: string) => `@${display}`}
                style={{ backgroundColor: 'rgba(63, 63, 220, 0.2)', borderRadius: 4, padding: '0 2px' }}
                renderSuggestion={(suggestion, query) => {
                  const name = String(suggestion.display ?? suggestion.id ?? 'gemma');
                  const q = (query ?? '').toLowerCase();
                  const nm = name.toLowerCase();
                  const n = q.length > 0 && nm.startsWith(q) ? q.length : 0;
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <GemmaAvatar size={22} />
                      <Box component="span" sx={{ fontWeight: 700, color: '#020242' }}>
                        @
                        {n > 0 ? name.slice(0, n) : ''}
                      </Box>
                      <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary', opacity: 0.72 }}>
                        {n > 0 ? name.slice(n) : name}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        asystent AI
                      </Typography>
                    </Box>
                  );
                }}
              />
            </MentionsInput>
          </Box>
          <VoiceInputButton
            isRecording={voiceDictation.isRecording}
            isSupported={voiceDictation.isSupported}
            disabled={!activeConversation || sending}
            onClick={voiceDictation.toggle}
            size={36}
            errorText={voiceDictation.error}
          />
          {(() => {
            /* Blokuj przycisk tylko gdy Gemma streamuje — zwykłe wiadomości mogą iść równolegle. */
            const canSend = !!activeConversation && composerHasSendableText && !gemmaThinking;
            const color = canSend ? '#3F3FDC' : '#B9B9B9';
            return (
              <Box
                onClick={()=>{ if (canSend) handleSend(); }}
                sx={{
                  mr: 0.5,
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color,
                  cursor: canSend ? 'pointer' : 'default',
                  userSelect: 'none',
                  alignSelf: 'center'
                }}
              >
                <SendSvg width={20} height={20} />
              </Box>
            );
          })()}
        </Paper>
      </Box>
      <Dialog
        open={!!previewUrl}
        onClose={()=>setPreviewUrl(null)}
        fullScreen
        sx={{ zIndex: 2147484005 }}
        BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.9)' } }}
        PaperProps={{ sx: { backgroundColor: 'black' } }}
      >
        <Box
          sx={{ p: 0, bgcolor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}
          onClick={()=>setPreviewUrl(null)}
        >
          {previewUrl && (
            <Box component="img" src={previewUrl} alt="Podgląd" sx={{ maxWidth: '100vw', maxHeight: '100vh', width: 'auto', height: 'auto' }} />
          )}
        </Box>
      </Dialog>
      <Snackbar
        open={!!gemmaSaveWarn}
        autoHideDuration={12000}
        onClose={() => setGemmaSaveWarn(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setGemmaSaveWarn(null)} sx={{ width: '100%', maxWidth: 560 }}>
          {gemmaSaveWarn}
        </Alert>
      </Snackbar>
    </Box>
  );
}


